import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './page-scrollbar.module.css';

const MIN_THUMB_SIZE = 48;
const TRACK_PADDING = 14;
const IDLE_RELAX_DELAY = 900;

type ScrollbarSurface = 'dark' | 'light';

type ScrollState = {
  clientHeight: number;
  scrollHeight: number;
  scrollTop: number;
};

function getScrollElement() {
  return document.scrollingElement ?? document.documentElement;
}

function setInstantScrollTop(top: number) {
  const root = document.documentElement;
  const previousScrollBehavior = root.style.scrollBehavior;

  root.style.scrollBehavior = 'auto';
  getScrollElement().scrollTop = top;
  root.style.scrollBehavior = previousScrollBehavior;
}

function getScrollState(): ScrollState {
  const scrollElement = getScrollElement();

  return {
    clientHeight: window.innerHeight,
    scrollHeight: scrollElement.scrollHeight,
    scrollTop: scrollElement.scrollTop,
  };
}

function getVisibleScrollbarSurface(): ScrollbarSurface {
  const probeY = window.innerHeight / 2;
  const surface = Array.from(document.querySelectorAll<HTMLElement>('[data-header-surface]')).find(
    (element) => {
      const rect = element.getBoundingClientRect();
      return rect.top <= probeY && rect.bottom > probeY;
    },
  );

  return surface?.dataset.headerSurface === 'dark' ? 'dark' : 'light';
}

export function PageScrollbar() {
  const [scrollState, setScrollState] = useState<ScrollState | null>(null);
  const [surface, setSurface] = useState<ScrollbarSurface>('light');
  const [isActive, setIsActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const idleTimerRef = useRef<number | null>(null);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current === null) return;
    window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
  }, []);

  const revealTemporarily = useCallback(() => {
    clearIdleTimer();
    setIsActive(true);
    idleTimerRef.current = window.setTimeout(() => {
      setIsActive(false);
      idleTimerRef.current = null;
    }, IDLE_RELAX_DELAY);
  }, [clearIdleTimer]);

  useEffect(() => {
    const overlayQuery = window.matchMedia(
      '(pointer: fine) and (min-width: 901px) and (prefers-reduced-motion: no-preference)',
    );

    let frame = 0;
    const update = () => {
      frame = 0;
      setScrollState(getScrollState());
      setSurface(getVisibleScrollbarSurface());
    };
    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    const syncOverlayMode = () => {
      if (overlayQuery.matches) {
        document.documentElement.dataset.pageScrollbarEnabled = 'true';
      } else {
        clearIdleTimer();
        setIsActive(false);
        delete document.documentElement.dataset.pageScrollbarEnabled;
      }
      scheduleUpdate();
    };
    const handleScroll = () => {
      if (overlayQuery.matches) revealTemporarily();
      scheduleUpdate();
    };

    syncOverlayMode();
    overlayQuery.addEventListener('change', syncOverlayMode);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('landing-surface-change', scheduleUpdate);

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
    resizeObserver?.observe(document.documentElement);
    resizeObserver?.observe(document.body);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      overlayQuery.removeEventListener('change', syncOverlayMode);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('landing-surface-change', scheduleUpdate);
      resizeObserver?.disconnect();
      clearIdleTimer();
      delete document.documentElement.dataset.pageScrollbarEnabled;
    };
  }, [clearIdleTimer, revealTemporarily]);

  const geometry = useMemo(() => {
    if (!scrollState || scrollState.scrollHeight <= scrollState.clientHeight) return null;

    const trackHeight = Math.max(0, scrollState.clientHeight - TRACK_PADDING * 2);
    const thumbHeight = Math.min(
      trackHeight,
      Math.max(MIN_THUMB_SIZE, (scrollState.clientHeight / scrollState.scrollHeight) * trackHeight),
    );
    const maxScrollTop = scrollState.scrollHeight - scrollState.clientHeight;
    const maxThumbTop = trackHeight - thumbHeight;
    const thumbTop = maxScrollTop > 0 ? (scrollState.scrollTop / maxScrollTop) * maxThumbTop : 0;

    return { maxScrollTop, maxThumbTop, thumbHeight, thumbTop, trackHeight };
  }, [scrollState]);

  if (!geometry) return null;

  const scrollProgress = Math.max(
    0,
    Math.min((scrollState?.scrollTop ?? 0) / geometry.maxScrollTop, 1),
  );

  const scrollToThumbPosition = (clientY: number) => {
    const track = trackRef.current;
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const rawTop = clientY - rect.top - geometry.thumbHeight / 2;
    const thumbTop = Math.max(0, Math.min(rawTop, geometry.maxThumbTop));
    const scrollRatio = geometry.maxThumbTop > 0 ? thumbTop / geometry.maxThumbTop : 0;

    revealTemporarily();
    setInstantScrollTop(scrollRatio * geometry.maxScrollTop);
  };

  const handleTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    scrollToThumbPosition(event.clientY);
  };

  const handleThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const startY = event.clientY;
    const startScrollTop = getScrollElement().scrollTop;
    const pointerId = event.pointerId;
    const thumb = event.currentTarget;

    setIsDragging(true);
    setIsActive(true);
    clearIdleTimer();
    thumb.setPointerCapture(pointerId);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const scrollRatio =
        geometry.maxThumbTop > 0 ? geometry.maxScrollTop / geometry.maxThumbTop : 0;
      const nextTop = Math.max(
        0,
        Math.min(startScrollTop + deltaY * scrollRatio, geometry.maxScrollTop),
      );
      setInstantScrollTop(nextTop);
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      revealTemporarily();
      if (thumb.hasPointerCapture(pointerId)) thumb.releasePointerCapture(pointerId);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  };

  return (
    <>
      <div
        ref={trackRef}
        className={styles.track}
        data-page-scrollbar-track
        data-page-scrollbar-surface={surface}
        data-visible={isActive || isHovered || isDragging ? 'true' : undefined}
        data-hovered={isHovered || isDragging ? 'true' : undefined}
        data-dragging={isDragging ? 'true' : undefined}
        style={{ height: geometry.trackHeight }}
        aria-hidden='true'
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
        onPointerDown={handleTrackPointerDown}
      >
        <div
          className={styles.thumb}
          data-page-scrollbar-thumb
          style={{
            height: geometry.thumbHeight,
            transform: `translate3d(0, ${geometry.thumbTop}px, 0)`,
          }}
          onPointerDown={handleThumbPointerDown}
        />
      </div>

      <div
        className={styles.mobileProgress}
        data-page-scroll-progress
        data-page-scrollbar-surface={surface}
        aria-hidden='true'
      >
        <span
          className={styles.mobileProgressFill}
          data-page-scroll-progress-fill
          style={{ transform: `scaleY(${scrollProgress})` }}
        />
      </div>
    </>
  );
}
